<template>
  <div class="full-height">
    <v-card flat rounded="0" class="full-height pa-6">
      <div class="d-flex justify-space-between align-center mb-6">
        <div class="d-flex justify-start align-center">
          <v-icon large dark class="pa-2 brand-background border-radius"
            >mdi-chart-bar-stacked</v-icon
          >
          <Typography variant="h-1" class="py-0 ml-4"> Reporting </Typography>
        </div>
        <div class="d-flex justify-end align-center">
          <v-autocomplete
            outlined
            dense
            hide-details
            :items="clients"
            item-text="formattedName"
            item-value="clientId"
            label="Client"
            v-model="clientId"
            v-if="userProfile.clientId === 1"
            class="client-dropdown"
          ></v-autocomplete>
          <Button
            color="brandPrimary"
            class="ml-6"
            @click="loadReportForm"
            v-if="hasPermission('reports:create:report')"
          >
            <v-icon left>mdi-plus</v-icon>
            New Report
          </Button>
        </div>
      </div>

      <v-divider class="mb-6" />
      <div class="your-reports-tab">
        <Typography variant="body-1" class="your-reports-label primary--text"
          >YOUR REPORTS</Typography
        >
      </div>
      <v-divider />
      <div class="mt-6">
        <transition :name="transitionDirection" mode="out-in">
          <div v-if="showTable" class="d-flex" key="1">
            <div class="table mr-6 flex-grow-1">
              <v-data-table
                :headers="columns"
                :items="filteredReports"
                item-key="reportId"
                class="elevation-0 row-pointer report-table"
                @click:row="loadReport"
                :loading="loadingReportTable"
                no-data-text="No reports found"
              >
                <template v-slot:[`item.updatedAt`]="{ item }">
                  {{ niceDate(item.updatedAt) }}
                </template>
              </v-data-table>
            </div>
            <div
              class="custom-report-text d-flex flex-column ml-6 text-left"
              style="width: 424px; min-width: 424px"
            >
              <Typography variant="h-3" class="mb-6">Custom Reports</Typography>
              <Typography variant="body-1" class="mb-6">
                Empower your organization by providing the right people with the right data.<br /><br />
                One of {{ applicationName }}'s unique capabilities is providing custom data
                visualization tools for all the activity connected with the
                {{ applicationName }} platform and from data sources outside of
                {{ applicationName }}. And with a team of data scientists, designers and engineers
                who specialize in managing program data, {{ applicationName }} can help your
                organization discover new insights and create new tools that increase the success of
                your company.
              </Typography>
              <CustomerServiceContact />
            </div>
          </div>
          <div v-else-if="showReport" key="2">
            <div class="d-flex justify-space-between align-center">
              <Button text color="primary" class="px-2 mb-6" @click="leaveReport">
                <v-icon left>mdi-chevron-left</v-icon>
                Back to Reports
              </Button>
            </div>
            <iframe
              v-if="currentProvider === 'LOOKER'"
              class="ds-report"
              :width="windowWidth"
              :height="windowHeight"
              :src="currentReportSource"
              frameborder="0"
              style="border: 0"
              allowfullscreen
            ></iframe>
            <PowerBIReport
              v-else-if="currentProvider === 'POWERBI'"
              :config="currentPBIConfig"
              width="100%"
              :height="windowHeight"
            />
          </div>
        </transition>
      </div>
    </v-card>
    <CreateReportForm
      v-model="createReportWidget"
      :clients="clients"
      :userId="userProfile.userId"
      :defaultClientId="clientId"
      @created="getReports"
    />
    <!-- Dialog used for displaying loading message. -->
    <LoadingDialog
      v-if="showLoadingDialog"
      :showDialog="showLoadingDialog"
      :header="loadingHeaderMessage"
      :line1="loadingMessageLine1"
      :line2="loadingMessageLine2"
      :messageArray="loadingMessageArray"
    ></LoadingDialog>
  </div>
</template>

<script>
import LoadingDialog from "@/components/loadingDialog.vue";
import CreateReportForm from "@/components/reports/CreateReportForm.vue";
import PowerBIReport from "@/components/reports/PowerBIReport.vue";
import ReportService from "../services/ReportService.js";
import PowerBIReportService from "../services/PowerBIReportService.js";
import isOne10PointsEnabled from "@/mixins/one10Points/isOne10PointsEnabled.js";
import permissionsMixin from "@/mixins/permissionsMixin";
import whitelabelMixin from "@/mixins/whitelabelMixin";
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import CustomerServiceContact from "@/components/help/CustomerServiceContact.vue";

import moment from "moment";

import { mapState } from "vuex";

export default {
  name: "Reporting",
  title: "Reporting",
  mixins: [isOne10PointsEnabled, permissionsMixin, whitelabelMixin],
  components: {
    LoadingDialog,
    CreateReportForm,
    PowerBIReport,
    Button,
    Typography,
    CustomerServiceContact,
  },
  data() {
    return {
      windowHeight: 700,
      windowWidth: 700,
      transitionDirection: "topic-right",
      showLoadingDialog: false,
      loadingHeaderMessage: null,
      loadingMessageLine1: null,
      loadingMessageLine2: null,
      loadingMessageArray: [],
      columns: [
        {
          text: "Report Name",
          align: "start",
          value: "displayName",
          sortable: false,
          width: "60%",
        },
        {
          text: "Description",
          value: "description",
          width: "40%",
          sortable: false,
        },
        // {
        //   text: "Report Date",
        //   value: "updatedAt",
        //   width: "30%",
        //   sortable: false
        // }
      ],
      reports: [],
      showTable: true,
      showReport: false,
      currentReportSource: null,
      currentProvider: null,
      currentPBIConfig: null,
      loadingReportTable: true,
      createReportWidget: false,
    };
  },

  created() {
    this.windowWidth = window.innerWidth * 0.8;
    this.windowHeight = this.windowWidth * 0.6;
    // this.clientId = this.userProfile.clientId;
    // 1250 / 750
    // 1.66667 ratio

    //This listener is used to detect screen size changes so the report is resized
    window.addEventListener("resize", this.resizeReport);

    this.getReports();
  },
  mounted() {
    this.windowWidth = window.innerWidth * 0.8;
    this.windowHeight = this.windowWidth * 0.6;
    console.log("Mounted");

    //Move the fresh works help widget
    var freshworks = document.querySelector("#launcher-frame");
    if (freshworks) {
      freshworks.style.right = "-55px";
      freshworks.style["max-width"] = freshworks.style["min-width"] = "90px";
    }
    // window.divNode = window.document.createElement("div");
    // console.log(window.divNode);
    // window.divNode.setAttribute("id", "tempFreshworks");
    // window.divNode.innerHTML =
    //   "<style>#launcher-frame { right: -55px !important; }</style>";
    // window.document.body.appendChild(window.divNode);
  },
  unmounted() {
    window.removeEventListener("resize", this.resizeReport);
    // window.divNode.parentNode.removeChild(window.divNode);
    var freshworks = document.querySelector("#launcher-frame");
    if (freshworks) {
      freshworks.style.right = "22px";
    }
  },
  methods: {
    niceDate(date) {
      var newDate = moment(date).local().format("MMMM Do YYYY");
      return newDate;
    },

    async getReports() {
      this.loadingReportTable = true;

      const [lookerReports, pbiConfigs] = await Promise.all([
        ReportService.getReports().catch(() => []),
        PowerBIReportService.getConfigs().catch(() => []),
      ]);

      const looker = (Array.isArray(lookerReports) ? lookerReports : [])
        .filter((x) => x.status !== "Deleted")
        .map((r) => ({ ...r, provider: "LOOKER" }));

      const powerbi = (Array.isArray(pbiConfigs) ? pbiConfigs : [])
        .filter((x) => x.enabled)
        .map((r) => ({
          ...r,
          displayName: r.displayName || r.name,
          description: r.description || "",
          provider: "POWERBI",
        }));

      const reports = [...looker, ...powerbi].sort((a, b) => {
        const dateA = new Date(a.updateDate || a.updatedAt || 0).getTime();
        const dateB = new Date(b.updateDate || b.updatedAt || 0).getTime();
        return dateB - dateA;
      });

      this.reports = reports;
      this.loadingReportTable = false;

      var autoLoadReport = reports.find((x) => x.openOnLoad);
      if (autoLoadReport != undefined) {
        console.log("Loading report");
        this.loadReport(autoLoadReport);
      }
    },

    resizeReport(e) {
      this.windowWidth = e.currentTarget.innerWidth * 0.8;
      this.windowHeight = this.windowWidth * 0.6;
    },

    loadReport(row) {
      if (row.provider === "POWERBI") {
        this.loadPowerBIReport(row);
      } else {
        this.loadLookerReport(row);
      }
    },
    loadPowerBIReport(row) {
      this.transitionDirection = "topic-right";
      this.currentProvider = "POWERBI";
      this.currentPBIConfig = row;
      this.showReport = true;
      this.showTable = false;
    },
    loadLookerReport(row) {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Loading Report";
      this.loadingMessageLine1 = "This should just take a second!";
      this.loadingMessageLine2 = "";
      this.loadingMessageArray = [
        "We're busy loading your report",
        "Loading the data",
        "We're making your report lightning fast",
        "We're almost done!",
      ];
      console.log(row.parameterColumn, row.reportId, this.$auth.user.sub);
      // We check if we have any tokens for this report
      if (this.reportTokens.length > 0 && this.reportTokens[0] != null) {
        var existingTokens = this.reportTokens.filter(
          (x) => x && x.meta && x.meta.reportId == row.reportId
        );
        var getToken = false;

        // We sort to get the most recent token for this report (if any) and check to make sure it isn't expired
        var existingToken =
          existingTokens.length == 0
            ? null
            : existingTokens.sort((a, b) => b.meta.createdAt - a.meta.createdAt)[0];

        if (
          existingToken != null &&
          (existingToken.meta.tokenTTL == null ||
            moment(existingToken.meta.createdAt).add(existingToken.meta.tokenTTL - 180, "seconds") <
              moment().utc())
        ) {
          getToken = true;
        } else if (existingToken == null) {
          getToken = true;
        }
      } else {
        getToken = true;
      }
      console.log("Fetching token? ", getToken);
      if (getToken) {
        let user = this.userProfile;
        console.log("Value for parameter ", user[row.parameterColumn]);
        ReportService.getToken(
          row.reportId,
          this.userProfile.userId,
          row.parameterColumn,
          user[row.parameterColumn],
          this.userProfile.Client
        ).then((response) => {
          console.log(response);

          this.transitionDirection = "topic-right";
          this.currentProvider = "LOOKER";
          this.currentReportSource =
            row.reportUrl +
            '?params={"token1": "' +
            response.token +
            '", "token2": "' +
            response.token +
            '", "token3": "' +
            response.token +
            '", "token4": "' +
            response.token +
            '", "token5": "' +
            response.token +
            (this.isOne10PointsEnabled() ? '", "token6": "' + response.token : "") +
            '"}';
          console.log("Current Report Source:");
          console.log(this.currentReportSource);

          this.showLoadingDialog = false;
          if (row.openInNewTab) {
            var tabUrl = this.currentReportSource.replace("/embed", "");
            window.open(tabUrl, "_blank");
          } else {
            this.showReport = true;
            this.showTable = false;
          }
        });
      } else {
        this.transitionDirection = "topic-right";
        this.currentProvider = "LOOKER";
        this.currentReportSource =
          row.reportUrl +
          '?params={"token1": "' +
          existingToken.token +
          '", "token2": "' +
          existingToken.token +
          '", "token3": "' +
          existingToken.token +
          '", "token4": "' +
          existingToken.token +
          '", "token5": "' +
          existingToken.token +
          (this.isOne10PointsEnabled() ? '", "token6": "' + existingToken.token : "") +
          '"}';
        console.log("Current Report Source:");
        console.log(this.currentReportSource);

        this.showLoadingDialog = false;
        if (row.openInNewTab) {
          var tabUrl = this.currentReportSource.replace("/embed", "");
          window.open(tabUrl, "_blank");
        } else {
          this.showReport = true;
          this.showTable = false;
        }
      }
    },
    leaveReport() {
      this.transitionDirection = "topic-left";
      this.showTable = true;
      this.showReport = false;
      this.currentReportSource = null;
      this.currentProvider = null;
      this.currentPBIConfig = null;
    },
    loadReportForm() {
      this.createReportWidget = true;
    },
  },
  computed: {
    ...mapState(["userProfile", "clients", "reportTokens", "globalClientId", "featureFlags"]),
    isPowerBiReportsEnabled() {
      return this.featureFlags["feature-power-bi-reports"];
    },
    clientId: {
      get: function () {
        return this.globalClientId;
      },
      set: function (newVal) {
        this.$store.dispatch("setClientId", newVal);
      },
    },
    filteredReports() {
      // If not Whistle, then we want to show universal + their client.
      // Otherwise, we show based on the clientId dropdown selector
      let reports;
      if (this.userProfile.clientId != 1 || this.clientId == null) {
        reports = this.reports.filter(
          (x) =>
            [0, this.userProfile.clientId].includes(x.clientId) &&
            (x.type !== "PROCORE" || this.userProfile?.Client?.procoreEnabled)
        );
      } else {
        reports = this.reports.filter((x) => x.clientId == this.clientId);
      }
      // Hide Power BI reports unless the feature flag is enabled
      if (!this.isPowerBiReportsEnabled) {
        reports = reports.filter((x) => x.provider !== "POWERBI");
      }
      return reports;
    },
  },
};
</script>
<style scoped>
.topic-left-enter {
  opacity: 0;
  transform: translateX(100%);
}

.topic-right-enter {
  opacity: 0;
  transform: translateX(-100%);
}

.topic-left-enter-active,
.topic-right-enter-active {
  transition: all 0.65s cubic-bezier(0.19, 1, 0.22, 1);
}

.table {
  width: 90%;
}
.custom-report-text {
  width: 424px;
  min-width: 424px;
}
.your-reports-tab {
  display: flex;
  align-items: flex-end;
}
.your-reports-label {
  display: inline-block;
  padding: 9px 16px;
  font-size: 14px;
  font-weight: 350;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  border-bottom: 2px solid var(--v-primary-base);
}
.remove-underline {
  text-decoration: none;
}

.brand-background {
  background-color: var(--v-brand-base);
}
.border-radius {
  border-radius: 5px;
}

.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
.client-dropdown {
  width: 300px;
  min-width: 300px;
}

.report-table {
  border: solid 1px;
  border-color: lightgray;
  border-radius: 0;
}

.report-table >>> .v-data-table-header {
  background-color: white;
}
</style>
<style lang="css" scoped></style>
